import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../services/api';
import PublicThemeRenderer from '../../components/public/PublicThemeRenderer';
import './PublicBusinessPage.css';

const SITE_NAME = 'Digital Business Kit';
const BULB_COUNT = 9;

// Minimal head-tag helpers — enough for OG/SEO without pulling in react-helmet.
// Each setter returns the node only if it created a new one, so cleanup never
// disturbs tags that already existed on the page for another reason.
const setMetaTag = (attr, key, content) => {
  if (!content) return null;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  const created = !el;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  return created ? el : null;
};

const setLinkTag = (rel, href) => {
  if (!href) return null;
  let el = document.querySelector(`link[rel="${rel}"]`);
  const created = !el;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  return created ? el : null;
};

const PublicBusinessPage = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorKind, setErrorKind] = useState('notfound'); // 'notfound' | 'network'
  const [retryToken, setRetryToken] = useState(0);

  const injectedNodes = useRef([]);
  const originalTitle = useRef(document.title);

  const handleRetry = useCallback(() => setRetryToken((t) => t + 1), []);

  // ---- Fetch (guarded against stale responses & unmount) ----
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    setLoading(true);
    setError(null);
    setData(null);
    window.scrollTo(0, 0);

    const fetchPublicData = async () => {
      try {
        const res = await api.get(`/business/${slug}`, { signal: controller.signal });
        if (cancelled) return;

        if (res.success) {
          setData(res.data);
        } else {
          setErrorKind('notfound');
          setError('Business page not found');
        }
      } catch (err) {
        if (cancelled) return;
        // Axios rejects with a plain "Network Error" (no response) when the
        // request never reached the server — that case is worth a retry button,
        // an actual 404-style message from the API is not.
        const isNetworkIssue = !err.message || /network/i.test(err.message);
        setErrorKind(isNetworkIssue ? 'network' : 'notfound');
        setError(err.message || 'This page could not be reached');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchPublicData();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [slug, retryToken]);

  // ---- SEO / document head, only once we actually have business data ----
  useEffect(() => {
    if (!data?.business) return;
    const { business } = data;

    document.title = `${business.businessName}${business.category ? ` | ${business.category}` : ''} — ${SITE_NAME}`;

    const previewImage = business.coverImage || business.logo || '';
    const nodes = [
      setMetaTag('name', 'description', business.description || `${business.businessName} on ${SITE_NAME}`),
      setMetaTag('property', 'og:title', business.businessName),
      setMetaTag('property', 'og:description', business.description || ''),
      setMetaTag('property', 'og:type', 'business.business'),
      previewImage ? setMetaTag('property', 'og:image', previewImage) : null,
      setMetaTag('name', 'twitter:card', 'summary_large_image'),
      setLinkTag('canonical', window.location.href),
      business.logo ? setLinkTag('icon', business.logo) : null
    ].filter(Boolean);

    // Structured data helps the business actually show up right in search results.
    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: business.businessName,
      description: business.description || undefined,
      image: business.logo || undefined,
      telephone: business.phone || undefined,
      email: business.email || undefined,
      url: window.location.href,
      address: {
        '@type': 'PostalAddress',
        streetAddress: business.address || undefined,
        addressLocality: business.city || undefined,
        addressRegion: business.state || undefined,
        addressCountry: business.country || undefined
      }
    });
    document.head.appendChild(ld);
    nodes.push(ld);
    injectedNodes.current = nodes;

    return () => {
      injectedNodes.current.forEach((node) => node.remove());
      injectedNodes.current = [];
      document.title = originalTitle.current;
    };
  }, [data]);

  if (loading) {
    return <BusinessPageSkeleton />;
  }

  if (error || !data) {
    return (
      <BusinessPageError
        kind={errorKind}
        detail={error}
        onRetry={errorKind === 'network' ? handleRetry : undefined}
      />
    );
  }

  return (
    <div className="public-page-enter">
      <PublicThemeRenderer
        business={data.business}
        categories={data.categories}
        products={data.products}
        isPreview={false}
      />
    </div>
  );
};

// A row of marquee bulbs, like the chase-lights over an Indian shopfront sign.
const BulbRow = ({ dim }) => (
  <div className={`pbp-bulb-row${dim ? ' is-dim' : ''}`} aria-hidden="true">
    {Array.from({ length: BULB_COUNT }).map((_, i) => (
      <span key={i} className="pbp-bulb" style={{ '--i': i }} />
    ))}
  </div>
);

// The waiting room: a shopfront card whose shutter is either rising (data on
// its way up) or rolled down (nothing to show yet). Mirrors the real page's
// layout beneath the shutter so the reveal reads as continuous, not a swap.
const BusinessPageSkeleton = () => (
  <div className="pbp-waiting-room" data-state="opening">
    <span className="sr-only" role="status" aria-live="polite">Loading business page…</span>

    <div className="pbp-shopfront-card is-opening">
      <div className="pbp-awning">
        <BulbRow />
        <span className="pbp-awning-label">Status: opening</span>
      </div>

      <div className="pbp-shutter-window">
        <div className="pbp-shutter-door is-rising" />
        <div className="pbp-stage">
          <div className="pbp-stage-avatar shimmer" />
          <div className="pbp-stage-line pbp-stage-line-title shimmer" />
          <div className="pbp-stage-line pbp-stage-line-pill shimmer" />
          <div className="pbp-stage-line pbp-stage-line-desc shimmer" />
          <div className="pbp-stage-actions">
            <div className="pbp-stage-action shimmer" />
            <div className="pbp-stage-action shimmer" />
            <div className="pbp-stage-action shimmer" />
          </div>
          <div className="pbp-stage-grid">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="pbp-stage-card shimmer" />
            ))}
          </div>
        </div>
      </div>
    </div>

    <footer className="pbp-waiting-footer">Powered by <strong>{SITE_NAME}</strong></footer>
  </div>
);

const BusinessPageError = ({ kind, detail, onRetry }) => {
  const isNetwork = kind === 'network';

  return (
    <div className="pbp-waiting-room" data-state="closed">
      <div className="pbp-shopfront-card is-closed">
        <div className="pbp-awning">
          <BulbRow dim />
          <span className="pbp-awning-label">
            {isNetwork ? 'Status: connection lost' : 'Status: closed'}
          </span>
        </div>

        <div className="pbp-shutter-window">
          <div className="pbp-shutter-door is-down">
            <span className="pbp-shutter-lock" aria-hidden="true" />
          </div>
          <div className="pbp-hang-tag" aria-hidden="true">
            <span>{isNetwork ? 'Offline' : 'Not found'}</span>
          </div>
        </div>
      </div>

      <div className="pbp-notice">
        <h2 className="pbp-notice-title">
          {isNetwork ? "Couldn't reach the server" : "This shopfront isn't open right now"}
        </h2>
        <p className="pbp-notice-copy">
          {isNetwork
            ? 'Check your connection and try again — the page will reload as soon as we hear back.'
            : 'It may have been unpublished, moved, or the link is out of date — worth checking the address with the business owner.'}
        </p>
        {detail && !isNetwork && <p className="pbp-notice-detail">{detail}</p>}

        <div className="pbp-notice-actions">
          {onRetry && (
            <button onClick={onRetry} className="btn btn-primary">
              Try again
            </button>
          )}
          <Link to="/" className="btn btn-secondary">
            Back to {SITE_NAME}
          </Link>
        </div>
      </div>

      <footer className="pbp-waiting-footer">Powered by <strong>{SITE_NAME}</strong></footer>
    </div>
  );
};

export default PublicBusinessPage;